import { useEffect, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDealer } from '../../context/DealerContext';
import { dealerApi, DealerDocument, DocumentType } from '../../services/dealerApi';
import Money from '../../components/dealer/Money';
import { dealerErrorMessage, formatDate } from '../../utils/dealer';

const TYPES: DocumentType[] = ['PROFORMA', 'TAX_INVOICE', 'CREDIT_NOTE'];

/** Spec 085 (Flow J step 4, BR-30) — proformas (DPF-), tax invoices (DINV-) and credit notes (DCN-), presigned. */
export default function DealerDocumentsPage() {
  const { t, lang } = useLanguage();
  const { token } = useDealer();
  const [type, setType] = useState<DocumentType | ''>('');
  const [docs, setDocs] = useState<DealerDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setDocs(null);
    dealerApi.documents(token, type || undefined).then(setDocs).catch((err) => setError(dealerErrorMessage(err, t)));
  }, [token, type, t]);

  return (
    <div className="dealer-page">
      <div className="dealer-page__header">
        <div>
          <h2>{t('dealer.documents.title')}</h2>
          <p className="dealer-muted">{t('dealer.documents.subtitle')}</p>
        </div>
      </div>
      <div className="dealer-toolbar">
        <label htmlFor="doc-type">{t('dealer.documents.type')}</label>
        <select id="doc-type" className="dealer-select" value={type} onChange={(e) => setType(e.target.value as DocumentType | '')}>
          <option value="">{t('dealer.common.all')}</option>
          {TYPES.map((x) => <option key={x} value={x}>{t(`dealer.documents.type.${x}`)}</option>)}
        </select>
      </div>
      {error && <div className="error-message">{error}</div>}
      {!docs && !error && <p className="dealer-muted">{t('dealer.common.loading')}</p>}
      {docs && docs.length === 0 && <p className="dealer-muted dealer-empty-row">{t('dealer.documents.empty')}</p>}
      {docs && docs.length > 0 && (
        <div className="dealer-table-wrap">
          <table className="dealer-table">
            <thead>
              <tr>
                <th>{t('dealer.documents.number')}</th>
                <th>{t('dealer.documents.type')}</th>
                <th>{t('dealer.documents.amount')}</th>
                <th>{t('dealer.requests.created')}</th>
                <th aria-label={t('dealer.common.actions')}></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td dir="ltr">{d.number}</td>
                  <td>{t(`dealer.documents.type.${d.type}`)}</td>
                  <td><Money value={d.amountAed ?? null} /></td>
                  <td>{formatDate(d.createdAt, lang)}</td>
                  <td>
                    {d.downloadUrl
                      ? <a className="btn btn-secondary btn-sm" href={d.downloadUrl} target="_blank" rel="noopener noreferrer">{t('dealer.documents.download')}</a>
                      : <span className="dealer-muted">{t('dealer.documents.notReady')}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
